import React, { useState, useEffect } from 'react';
import { Modal, Button, Radio, message, Spin } from 'antd';
import { Download, FileArchive } from 'lucide-react';
import axios from 'axios';
import JSZip from 'jszip';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// Orthanc /studies/{id}/statistics returns DiskSize (bytes of stored DICOM)
async function fetchStudyStatistics(orthancId) {
  const resp = await axios.get(`/orthanc/studies/${orthancId}/statistics`);
  return resp.data; // { DiskSize, UncompressedSize, CountInstances, CountSeries, ... }
}

// Fetch all instance IDs for a study by iterating through its series
async function fetchStudyInstances(orthancId) {
  const studyResp = await axios.get(`/orthanc/studies/${orthancId}`);
  const seriesIds = studyResp.data.Series || [];
  if (seriesIds.length === 0) return [];

  const perSeries = await Promise.all(
    seriesIds.map(async (seriesId) => {
      const seriesResp = await axios.get(`/orthanc/series/${seriesId}`);
      return seriesResp.data.Instances || [];
    })
  );
  return perSeries.flat();
}

// Trigger a browser download from a URL
function triggerUrlDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ─── format card ────────────────────────────────────────────────────────────

const cardStyle = (selected) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  borderRadius: 8,
  border: selected ? '2px solid #3da295' : '1px solid #e5e7eb',
  background: '#ffffff',
  cursor: 'pointer',
  transition: 'border-color 0.2s, background 0.2s',
});

const FORMATS = [
  { value: 'dicom', label: 'DICOM', desc: 'Original medical imaging format (.zip)' },
  { value: 'jpeg',  label: 'JPEG',  desc: 'Compressed images, one per slice (.zip)' },
  { value: 'png',   label: 'PNG',   desc: 'Lossless images, one per slice (.zip)' },
];

// ─── component ──────────────────────────────────────────────────────────────

export function DownloadStudyDialog({ study, open, onOpenChange }) {
  const [selectedFormat, setSelectedFormat] = useState('dicom');
  const [stats, setStats]     = useState(null);   // Orthanc statistics
  const [loadingStats, setLoadingStats] = useState(false);
  const [downloading, setDownloading]   = useState(false);

  // Fetch real file size whenever dialog opens with a study
  useEffect(() => {
    if (!open || !study?.id) return;
    setStats(null);
    setLoadingStats(true);
    fetchStudyStatistics(study.id)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoadingStats(false));
  }, [open, study?.id]);

  // Derived sizes
  const dicomSize = stats ? formatBytes(stats.DiskSize) : (loadingStats ? '…' : '—');
  const imageCount = stats?.CountInstances || parseInt(study?.images, 10) || 0;
  // JPEG preview is ~50–150 KB per slice; PNG rendered is ~200–400 KB
  const jpegSize = imageCount ? formatBytes(imageCount * 100 * 1024) : '—';
  const pngSize  = imageCount ? formatBytes(imageCount * 300 * 1024) : '—';
  const sizeMap  = { dicom: dicomSize, jpeg: jpegSize, png: pngSize };

  const handleDownload = async () => {
    if (!study?.id) return;
    setDownloading(true);

    try {
      if (selectedFormat === 'dicom') {
        // Fetch the archive via authenticated axios so the Bearer token is sent.
        // A direct <a href> click carries no Authorization header and gets rejected.
        message.loading({ content: 'Preparing DICOM archive…', key: 'dl', duration: 0 });
        const resp = await axios.get(`/orthanc/studies/${study.id}/archive`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(resp.data);
        triggerUrlDownload(url, `${study.patientName || 'study'}_DICOM.zip`);
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        message.success({ content: 'DICOM archive downloaded', key: 'dl', duration: 3 });
        onOpenChange(false);

      } else {
        // Fetch all instance IDs then build a ZIP of JPEG/PNG previews
        const instanceIds = await fetchStudyInstances(study.id);
        if (instanceIds.length === 0) {
          message.warning('No image instances found for this study');
          setDownloading(false);
          return;
        }

        const zip = new JSZip();
        const folder = zip.folder(`${study.patientName || 'study'}_${selectedFormat.toUpperCase()}`);
        const ext    = selectedFormat === 'jpeg' ? 'jpg' : 'png';
        const total  = instanceIds.length;

        message.loading({ content: `Preparing ${total} images…`, key: 'dl', duration: 0 });

        // Process instances sequentially with a small concurrency window to avoid
        // overwhelming Orthanc with simultaneous requests
        const CONCURRENCY = 3;
        let savedCount = 0;
        for (let i = 0; i < instanceIds.length; i += CONCURRENCY) {
          const batch = instanceIds.slice(i, i + CONCURRENCY);
          await Promise.all(
            batch.map(async (instanceId, batchIdx) => {
              const idx = i + batchIdx;
              const endpoint = selectedFormat === 'jpeg'
                ? `/orthanc/instances/${instanceId}/preview`
                : `/orthanc/instances/${instanceId}/rendered`;
              try {
                const resp = await axios.get(endpoint, { responseType: 'arraybuffer' });
                folder.file(`image_${String(idx + 1).padStart(4, '0')}.${ext}`, resp.data);
                savedCount++;
              } catch {
                // Skip non-image instances (SR reports, Key Objects, Presentation States, etc.)
              }
            })
          );
          message.loading({ content: `Preparing images… (${Math.min(i + CONCURRENCY, total)}/${total})`, key: 'dl', duration: 0 });
        }

        if (savedCount === 0) {
          message.warning({ content: 'No renderable images found in this study', key: 'dl', duration: 4 });
          setDownloading(false);
          return;
        }

        const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 3 } });
        const url  = URL.createObjectURL(blob);
        triggerUrlDownload(url, `${study.patientName || 'study'}_${selectedFormat.toUpperCase()}.zip`);
        setTimeout(() => URL.revokeObjectURL(url), 60000);

        message.success({ content: `Downloaded ${savedCount} ${selectedFormat.toUpperCase()} image${savedCount !== 1 ? 's' : ''}`, key: 'dl', duration: 3 });
        onOpenChange(false);
      }
    } catch (err) {
      console.error('Download failed:', err);
      message.error({ content: 'Download failed — please try again', key: 'dl', duration: 4 });
    } finally {
      setDownloading(false);
    }
  };

  if (!study) return null;

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Download size={18} color="#3da295" />
          Download Study
        </span>
      }
      visible={open}
      onCancel={() => { if (!downloading) onOpenChange(false); }}
      width={500}
      footer={[
        <Button key="cancel" disabled={downloading} onClick={() => onOpenChange(false)}>
          Cancel
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<Download size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />}
          style={{ background: '#3da295', borderColor: '#3da295' }}
          loading={downloading}
          onClick={handleDownload}
        >
          Download
        </Button>,
      ]}
    >
      <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 14 }}>
        Choose the format to download this imaging study.
      </p>

      {/* ── Study Info ── */}
      <div style={{
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 16,
        marginBottom: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <FileArchive size={16} color="#6b7280" />
          <span style={{ fontWeight: 600, fontSize: 13 }}>Study Information</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 }}>
          <div><span style={{ color: '#6b7280' }}>Patient: </span>{study.patientName}</div>
          <div>
            <span style={{ color: '#6b7280' }}>Images: </span>
            {loadingStats ? <Spin size="small" /> : (stats?.CountInstances ?? study.images ?? 'N/A')} files
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={{ color: '#6b7280' }}>Study: </span>
            {study.modality} — {study.bodyPart || study.studyDescription || 'N/A'}
          </div>
        </div>
      </div>

      {/* ── Format Selection ── */}
      <div>
        <label style={{ display: 'block', fontWeight: 500, marginBottom: 12, fontSize: 14 }}>
          Select Download Format
        </label>
        <Radio.Group
          value={selectedFormat}
          onChange={(e) => setSelectedFormat(e.target.value)}
          style={{ width: '100%' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {FORMATS.map(({ value, label, desc }) => (
              <div
                key={value}
                style={cardStyle(selectedFormat === value)}
                onClick={() => setSelectedFormat(value)}
                onMouseEnter={(e) => { if (selectedFormat !== value) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Radio value={value} />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{label}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{desc}</div>
                  </div>
                </div>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {sizeMap[value]}
                </span>
              </div>
            ))}
          </div>
        </Radio.Group>
      </div>
    </Modal>
  );
}
